import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { User, Shield, Edit2, Save, X, Eye, EyeOff, KeyRound } from "lucide-react";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [editName, setEditName] = useState(false);
  const [nameForm, setNameForm] = useState({ name: user?.name || "", initials: user?.initials || "" });
  const [saving, setSaving] = useState(false);

  const [pwForm, setPwForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  const saveName = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put(`/users/${user._id}`, { name: nameForm.name, initials: nameForm.initials });
      setUser({ ...user, name: r.data.name, initials: r.data.initials });
      setEditName(false);
    } finally {
      setSaving(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwError("");
    setPwSuccess("");
    if (pwForm.new_password !== pwForm.confirm) {
      setPwError("Passwörter stimmen nicht überein");
      return;
    }
    if (pwForm.new_password.length < 6) {
      setPwError("Passwort mindestens 6 Zeichen");
      return;
    }
    setPwSaving(true);
    try {
      await api.put("/auth/change-password", {
        old_password: pwForm.old_password,
        new_password: pwForm.new_password,
      });
      setPwSuccess("Passwort erfolgreich geändert");
      setPwForm({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setPwError(err.response?.data?.detail || "Fehler beim Ändern des Passworts");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Mein Profil</h1>
        <p className="text-sm text-muted-foreground mt-1">Persönliche Einstellungen und Passwort</p>
      </div>

      {/* Profile Info */}
      <div className="bg-card border border-border p-6 mb-4 animate-in">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Profil-Informationen</h2>
          {!editName && (
            <button
              data-testid="edit-profile-button"
              onClick={() => { setEditName(true); setNameForm({ name: user?.name || "", initials: user?.initials || "" }); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary text-secondary-foreground text-xs font-semibold hover:bg-secondary/80"
            >
              <Edit2 size={13} /> Bearbeiten
            </button>
          )}
        </div>

        {/* Avatar */}
        <div className="flex items-center gap-4 mb-5">
          <div className="w-14 h-14 bg-primary/10 flex items-center justify-center">
            <span className="text-xl font-bold text-primary" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {user?.initials}
            </span>
          </div>
          <div>
            <p className="text-lg font-semibold">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        {editName ? (
          <form onSubmit={saveName} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Name *</label>
                <input
                  data-testid="profile-name-input"
                  value={nameForm.name}
                  onChange={e => setNameForm({ ...nameForm, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Kürzel *</label>
                <input
                  data-testid="profile-initials-input"
                  value={nameForm.initials}
                  onChange={e => setNameForm({ ...nameForm, initials: e.target.value.toUpperCase() })}
                  maxLength={4}
                  required
                  style={{ textTransform: "uppercase" }}
                  className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="save-profile-button" type="submit" disabled={saving}
                className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1">
                <Save size={12} /> {saving ? "..." : "Speichern"}
              </button>
              <button type="button" onClick={() => setEditName(false)}
                className="px-3 py-1.5 bg-secondary text-xs font-semibold flex items-center gap-1">
                <X size={12} /> Abbrechen
              </button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Name", value: user?.name },
              { label: "Kürzel", value: user?.initials },
              { label: "E-Mail", value: user?.email },
              { label: "Rolle", value: user?.role === "admin" ? "Administrator" : "Techniker" },
              { label: "Leserecht", value: user?.role === "admin" ? "Ja" : (user?.permissions?.read ? "Ja" : "Nein") },
              { label: "Schreibrecht", value: user?.role === "admin" ? "Ja" : (user?.permissions?.write ? "Ja" : "Nein") },
            ].map(f => (
              <div key={f.label}>
                <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{f.label}</p>
                <p className="text-sm mt-0.5">{f.value || "—"}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="bg-card border border-border p-6 animate-in">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={16} className="text-muted-foreground" />
          <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Passwort ändern</h2>
        </div>

        <form onSubmit={changePw} className="space-y-3 max-w-sm">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Aktuelles Passwort</label>
            <div className="relative">
              <input
                data-testid="profile-old-password"
                type={showOld ? "text" : "password"}
                value={pwForm.old_password}
                onChange={e => setPwForm({ ...pwForm, old_password: e.target.value })}
                required
                className="w-full px-3 py-2 pr-9 text-sm bg-background border border-input focus:border-primary focus:outline-none"
              />
              <button type="button" onClick={() => setShowOld(!showOld)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showOld ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Neues Passwort</label>
            <div className="relative">
              <input
                data-testid="profile-new-password"
                type={showNew ? "text" : "password"}
                value={pwForm.new_password}
                onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })}
                required
                className="w-full px-3 py-2 pr-9 text-sm bg-background border border-input focus:border-primary focus:outline-none"
              />
              <button type="button" onClick={() => setShowNew(!showNew)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Passwort bestätigen</label>
            <input
              data-testid="profile-confirm-password"
              type="password"
              value={pwForm.confirm}
              onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })}
              required
              className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
            />
          </div>

          {pwError && (
            <div className="text-xs text-destructive bg-destructive/10 px-3 py-2 border border-destructive/20">{pwError}</div>
          )}
          {pwSuccess && (
            <div className="text-xs text-green-600 bg-green-50 dark:bg-green-950/30 px-3 py-2 border border-green-200 dark:border-green-900">{pwSuccess}</div>
          )}

          <button
            data-testid="change-password-button"
            type="submit"
            disabled={pwSaving}
            className="px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
          >
            {pwSaving ? "Ändern..." : "Passwort ändern"}
          </button>
        </form>
      </div>
    </div>
  );
}
